import { PAGE_SUB_TYPES } from '@sparefoot/tracking-events';

/**
 * Mapping of SGP keywords to their tracking page types
 */
export const SGP_PAGE_BY_KEY = {
	'mini-self-storage': PAGE_SUB_TYPES.STORAGE_LANDING,
	'small-self-storage': PAGE_SUB_TYPES.STORAGE_LANDING,
	'medium-self-storage': PAGE_SUB_TYPES.STORAGE_LANDING,
	'large-self-storage': PAGE_SUB_TYPES.STORAGE_LANDING,
	'self-storage-lockers': PAGE_SUB_TYPES.STORAGE_LANDING,
};

/**
 * Convert keyword to title case
 * @param {string} keyword - The keyword (e.g., 'mini-self-storage', 'small-self-storage')
 * @returns {string} Title cased string (e.g., 'Mini Self Storage', 'Small Self Storage')
 */
const toTitleCase = (keyword) => {
	return keyword
		.split('-')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
};

/**
 * Build Review schema for SGP pages
 * @param {Object} pageContent - The page content object containing reviews
 * @returns {object|null} Review schema object or null if no reviews
 */
export const buildSGPReviewSchema = (pageContent) => {
	const reviews = pageContent?.reviews || [];

	if (reviews.length === 0) {
		return undefined;
	}

	return reviews.map((review) => ({
		'@type': 'Review',
		author: {
			'@type': 'Person',
			name: review.customerName || '',
		},
		datePublished: review.date || '',
		reviewRating: {
			'@type': 'Rating',
			ratingValue: review.stars?.toString() || '5',
			bestRating: '5',
			worstRating: '1',
		},
		reviewBody: review.text || '',
		url:
			review.link ||
			'https://www.trustpilot.com/review/www.selfstorage.com',
	}));
};

/**
 * Build FAQ schema for SGP pages
 * @param {Object} pageContent - The page content object containing FAQs in sections
 * @returns {object|null} FAQ schema object or null if no FAQs
 */
export const buildSGPFaqSchema = (pageContent) => {
	// Find the FAQ section in the sections array
	const faqSection = pageContent?.sections?.find((section) =>
		section.modules?.some((module) => module.type === 'faqs'),
	);

	// Extract FAQs from the FAQ module
	const faqModule = faqSection?.modules?.find(
		(module) => module.type === 'faqs',
	);
	const faqs = faqModule?.props?.content?.faqs || [];

	if (faqs.length === 0) {
		return null;
	}

	const faqSchema = {
		'@type': 'FAQPage',
		mainEntity: faqs.map((faq) => ({
			'@type': 'Question',
			name: faq.question,
			acceptedAnswer: {
				'@type': 'Answer',
				text: faq.answer,
			},
		})),
	};

	return faqSchema;
};

/**
 * Build page schema for SGP pages
 * @param {string} keyword - The original keyword from the route
 * @param {object} pageContent - The page content object for FAQ schema
 * @param {object} metadata - The metadata object
 * @returns {object} Page schema object
 */
export const buildSGPPageSchema = (keyword, pageContent, metadata) => {
	const pageUrl = `https://www.selfstorage.com/self-storage-sizes/${keyword}/`;
	const pageName = toTitleCase(keyword);
	const reviews = buildSGPReviewSchema(pageContent);

	return {
		'@context': 'https://schema.org',
		'@graph': [
			{
				'@type': 'WebPage',
				url: pageUrl,
				name: pageName,
				description: metadata?.description,
				inLanguage: 'en-US',
				isPartOf: {
					'@type': 'WebSite',
					name: 'http://SelfStorage.com',
					url: 'https://www.selfstorage.com/',
				},
			},
			{
				'@type': 'WebSite',
				name: 'http://SelfStorage.com',
				url: 'https://www.selfstorage.com/',
			},
			{
				'@type': 'BreadcrumbList',
				itemListElement: [
					{
						'@type': 'ListItem',
						position: 1,
						name: 'Home',
						item: 'https://www.selfstorage.com/',
					},
					{
						'@type': 'ListItem',
						position: 2,
						name: 'Self Storage Size Guide',
						item: 'https://www.selfstorage.com/self-storage-sizes/',
					},
					{
						'@type': 'ListItem',
						position: 3,
						name: pageName,
						item: pageUrl,
					},
				],
			},
			{
				'@type': 'Guide',
				name: pageName,
				url: pageUrl,
				description: metadata?.description,
				inLanguage: 'en-US',
				about: {
					'@type': 'Thing',
					name: 'Self Storage Units',
					url: 'https://www.selfstorage.com/self-storage-sizes/',
				},
			},
			{
				'@type': 'Organization',
				name: 'SelfStorage.com',
				url: 'https://www.selfstorage.com/',
				aggregateRating: {
					'@type': 'AggregateRating',
					ratingValue: '4.4',
					reviewCount: '77492',
					bestRating: '5',
					worstRating: '1',
				},
				...(reviews && { review: reviews }),
			},
			buildSGPFaqSchema(pageContent),
		].filter(Boolean),
	};
};
